import React, { useState, useEffect } from 'react';
import { 
  CalendarDaysIcon, 
  XMarkIcon, 
  CheckCircleIcon, 
  ClockIcon,
  VideoCameraIcon,
  XCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import tutorApi from './tutorApi';

// Helper to convert backend booking format to frontend format
const convertBackendBookings = (bookings) => {
  return bookings.map(booking => ({
    id: booking.id,
    studentId: booking.student,
    studentName: booking.student_name || 'Student',
    subject: booking.subject || booking.lesson_pack.subject,
    date: booking.date,
    startTime: booking.start_time,
    endTime: booking.end_time,
    status: booking.status.toLowerCase(),
    zoomLink: booking.meeting_link,
    meetingProvider: booking.meeting_provider,
    lessonPackId: booking.lesson_pack.id,
    lessonPackName: `${booking.lesson_pack.subject} - ${booking.lesson_pack.no_of_hours} hours`,
    duration_minutes: booking.duration_minutes,
    duration_hours: booking.duration_hours,
    student_display: booking.student_display || null,
    tutor_display: booking.tutor_display || null
  }));
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [error, setError] = useState(null);

  // Fetch bookings on component mount
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      setError(null);
      const data = await tutorApi.getBookings();
      const converted = convertBackendBookings(data);
      setBookings(converted);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleJoinMeeting = (booking) => {
    // Daily.co meetings - open in new tab
    if (booking.zoomLink) {
      window.open(booking.zoomLink, '_blank', 'noopener,noreferrer');
    }
  };


  const upcomingBookings = bookings.filter(b => b.status === 'upcoming');
  const pastBookings = bookings.filter(b => b.status === 'completed' || b.status === 'cancelled');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-3 w-3 mr-1" />
            Upcoming
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDisplayInTimezone = (display) => {
    if (!display || !display.date) return null;
    return `${formatDate(display.date)}, ${formatTime(display.start_time)} - ${formatTime(display.end_time)} (${display.timezone?.replace(/_/g, ' ') || 'UTC'})`;
  };

  const getBookingDuration = (startTime, endTime) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const startTotal = startHours * 60 + startMinutes;
    const endTotal = endHours * 60 + endMinutes;
    const durationMinutes = endTotal - startTotal;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
          <p className="text-gray-600 mt-1">View and join your scheduled tutoring sessions</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loadingBookings && (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      )}

      {/* Upcoming Bookings */}
      {!loadingBookings && upcomingBookings.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming Sessions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                      {booking.studentName}
                    </h3>
                    <p className="text-sm text-gray-600">{booking.subject}</p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-start text-sm">
                    <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                      {booking.tutor_display && (
                        <>
                          <span className="font-semibold text-gray-900 text-base">
                            {formatDisplayInTimezone(booking.tutor_display)}
                          </span>
                          {booking.student_display && (
                            <span className="text-xs text-gray-500">
                              Student&apos;s time: {formatDisplayInTimezone(booking.student_display)}
                            </span>
                          )}
                        </>
                      )}
                      {!booking.tutor_display && !booking.student_display && (
                        <span className="text-gray-600">{formatDate(booking.date)}, {formatTime(booking.startTime)} - {formatTime(booking.endTime)} ({getBookingDuration(booking.startTime, booking.endTime)})</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {formatTime(booking.startTime)} - {formatTime(booking.endTime)} ({getBookingDuration(booking.startTime, booking.endTime)})
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <VideoCameraIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {booking.zoomLink ? (
                      <button
                        onClick={() => handleJoinMeeting(booking)}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                      >
                        Join Meeting
                      </button>
                    ) : (
                      <span className="text-gray-400">Meeting link not available</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Upcoming Bookings */}
      {!loadingBookings && upcomingBookings.length === 0 && (
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-md p-8 border border-gray-200 text-center">
            <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Upcoming Sessions</h3>
            <p className="text-gray-600">You don't have any upcoming bookings scheduled.</p>
          </div>
        </div>
      )}

      {/* Past Bookings */}
      {!loadingBookings && pastBookings.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Past Sessions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200 opacity-75">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                      {booking.studentName}
                    </h3>
                    <p className="text-sm text-gray-600">{booking.subject}</p>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-start text-sm">
                    <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                      {booking.tutor_display && (
                        <>
                          <span className="font-semibold text-gray-900 text-base">
                            {formatDisplayInTimezone(booking.tutor_display)}
                          </span>
                          {booking.student_display && (
                            <span className="text-xs text-gray-500">
                              Student&apos;s time: {formatDisplayInTimezone(booking.student_display)}
                            </span>
                          )}
                        </>
                      )}
                      {!booking.tutor_display && !booking.student_display && (
                        <span className="text-gray-600">{formatDate(booking.date)}, {formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                    {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Meeting Component - Not needed for Daily.co (opens directly) */}
    </div>
  );
};

export default Bookings;

